import React, { useMemo } from "react";
import Box from "Components/Atoms/Box";
import "./styles.scss";
import Card, { CardProps } from "../Card";
import { Modify } from "utils/types";
import { useMediaQuery } from "@material-ui/core";
import Typography from "Components/Atoms/Typography";
import { useTranslation } from "react-i18next";
import EmptyState from "./Components/EmptyState";

const PI = Math.PI;

const SIZE = 200;

const HSIZE = 100;

const COLORS = ["#10CCB9", "#6369D1", "#EE4266", "#F6AE2D", "#E1EF1A"];

type DataSet = {
  id: string | number | null;
  label: string;
  value: number;
};

export type PieChartCardProps = {
  data: DataSet[];
  pieSize?: number | string;
  compactLegend?: boolean;
};

const PieChartCard = ({
  title,
  titleTranslation,
  data = [],
  pieSize = 56,
  compactLegend = false,
  ...props
}: Modify<CardProps, PieChartCardProps>) => {
  const { enhancedData = [], total = 0 } = useMemo(() => {
    let total = data.reduce((acc: number, cV) => acc + cV.value, 0);
    let past = 0;

    let arr = [];

    const visibleData =
      compactLegend && data.length > 4
        ? data.filter(({ value }) => value > 0)
        : data;
    const d = [...visibleData].sort((a, b) => b.value - a.value);

    for (let i = 0; i < d.length; i++) {
      const dataSet = d[i];
      let pct = total ? Math.ceil((dataSet.value / total) * 360) / 360 : 0;

      let rad = total ? (past / total) * 360 - 90 : -90;

      arr.push({ ...dataSet, pct, rad, color: COLORS[i % COLORS.length] });
      past += dataSet.value;
    }

    return { enhancedData: arr, total };
  }, [data, compactLegend]);

  const { t } = useTranslation(titleTranslation || "common");
  const isMobile = useMediaQuery("(max-width: 450px)");

  return (
    <Card
      {...props}
      className={[props.className, compactLegend ? "compact-pie-card" : ""]
        .filter(Boolean)
        .join(" ")}
      title={title}
      titleTranslation={titleTranslation}
      titleAlignment="center"
      hideHeader={isMobile}
      bodyProps={{ padding: "md", flex: true, className: "center flex-column" }}
    >
      {data.length && total ? (
        <>
          {isMobile && (
            <Typography
              variant="text-2"
              weight="bold"
              style={{ marginBottom: 16 }}
            >
              {title}
            </Typography>
          )}
          <svg
            height={pieSize}
            width={pieSize}
            style={{ marginBottom: !isMobile ? 24 : 12 }}
            viewBox={`0 0 ${SIZE} ${SIZE}`}
          >
            <circle r={HSIZE} cx={HSIZE} cy={HSIZE} fill="transparent"></circle>
            {enhancedData.map(({ id, pct, rad, color }) => (
              <circle
                key={id}
                r={HSIZE / 2}
                cx={HSIZE}
                cy={HSIZE}
                fill="transparent"
                stroke={color}
                strokeWidth={HSIZE}
                strokeDasharray={`calc(${pct} * ${HSIZE * PI}) ${HSIZE * PI}`}
                transform={`rotate(${rad} ${HSIZE} ${HSIZE})`}
              ></circle>
            ))}
          </svg>

          <Box
            flex
            width="auto"
            role={compactLegend ? "list" : undefined}
            aria-label={
              compactLegend && typeof title === "string" ? t(title) : undefined
            }
            tabIndex={compactLegend && enhancedData.length > 5 ? 0 : undefined}
            className={`flex-column ${
              compactLegend ? "compact-pie-legend" : ""
            }`}
            style={{
              alignItems: "flex-start",
              height: compactLegend ? undefined : "100%",
              maxWidth: "100%",
            }}
          >
            {enhancedData.map(({ id, label, color, value }) => (
              <div
                className={
                  compactLegend ? "compact-pie-legend-row" : "mg-bt-sm"
                }
                key={id}
                role={compactLegend ? "listitem" : undefined}
                title={`${label}: ${value}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: compactLegend ? "100%" : "auto",
                  whiteSpace: "nowrap",
                }}
              >
                <span
                  style={{
                    background: color,
                    flexShrink: 0,
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    marginRight: isMobile ? 8 : 16,
                  }}
                />
                <Typography
                  variant="text-3"
                  style={{
                    maxWidth: compactLegend ? undefined : "9ch",
                    minWidth: 0,
                    textOverflow: "ellipsis",
                    whiteSpace: compactLegend ? "normal" : "nowrap",
                    lineHeight: compactLegend ? "20px" : undefined,
                    display: compactLegend ? "-webkit-box" : undefined,
                    WebkitLineClamp: compactLegend ? 2 : undefined,
                    WebkitBoxOrient: compactLegend ? "vertical" : undefined,
                    overflow: "hidden",
                  }}
                  color={isMobile ? "subdued" : "textPrimary"}
                >
                  {label}
                </Typography>
                <Typography
                  variant="text-3"
                  translation={null}
                  color={isMobile ? "subdued" : "textPrimary"}
                  style={{ flexShrink: 0 }}
                >
                  : {value}
                </Typography>
              </div>
            ))}
          </Box>
        </>
      ) : (
        <>
          <EmptyState
            style={{ width: pieSize, height: pieSize, marginBottom: 16 }}
          />
          <Typography variant="text-3" textAlign="center" component="p">
            Currently, there is no data to show
          </Typography>
        </>
      )}
    </Card>
  );
};

export default PieChartCard;
